import React, { useState, useMemo } from "react";
import {
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import Loading from "./Loading";
import { useAuth } from "../../../../context";
import EditAsset from "./EditAsset";
import AddAsset from "./AddAsset";

const AssetList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [editData, setEditData] = useState("");

  const { data, isLoading, isError } = useFetchObjects(
    ["general-stock"],
    "general-stock",
    token,
  );
  const { handleDelete, ConfirmDialog } = useDelete("general-stock", token);

  const totalCost = useMemo(
    () =>
      data?.reduce((acc, item) => acc + Number(item?.total_cost || 0), 0) || 0,
    [data],
  );

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Grid container>
        <ErrorPage />
      </Grid>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ marginTop: 4 }}>
      <Paper elevation={3} sx={{ padding: 4, borderRadius: 2 }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Typography variant="h4" color="primary" fontWeight={700}>
            {t("hr.asset")}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleIcon style={{ margin: "0px 5px" }} />}
            onClick={() => {
              setOpenAdd(true);
            }}
          >
            {t("hr.addNew")}
          </Button>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Display the total cost */}
        <Typography
          variant="h6"
          color="secondary"
          sx={{ textAlign: "left", fontWeight: 600, mb: 2 }}
        >
          {t("hr.totalCost")}: {totalCost?.toLocaleString()} ؋
        </Typography>

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: "#ebe8e1" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>{t("hr.no")}</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {t("hr.name")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {t("hr.quantity")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {t("hr.purchaseDate")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {t("hr.costPerUnit")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {t("hr.totalCost")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {t("hr.location")}
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {t("hr.action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((item, i) => (
                <TableRow
                  key={i}
                  sx={{
                    backgroundColor:
                      i % 2 === 0 ? "action.hover" : "background.default",
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>{item.id}</TableCell>
                  <TableCell
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "15vw",
                    }}
                  >
                    <Typography margin={"0px 3px"}>{item?.name}</Typography>
                    <Box
                      component={"img"}
                      sx={{
                        width: "5vw",
                        height: "7vh",
                        cursor: "pointer",
                      }}
                      src={item?.image}
                      onClick={() => {
                        // Open image in a new tab
                        window.open(item?.image, "_blank");
                      }}
                    />
                  </TableCell>
                  <TableCell>{item?.quantity}</TableCell>
                  <TableCell>{item?.purchase_date}</TableCell>
                  <TableCell>{item?.cost_per_unit}</TableCell>
                  <TableCell>{item?.total_cost}</TableCell>
                  <TableCell>{item?.location}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      aria-label="edit"
                      onClick={() => {
                        setEditData(item);
                        setOpenEdit(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(item.id)}
                      aria-label="delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <ConfirmDialog />

      <AddAsset
        open={openAdd}
        onClose={() => {
          setOpenAdd(false);
        }}
      />

      <EditAsset
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setEditData(null);
        }}
        editData={editData}
      />
    </Container>
  );
};

export default AssetList;
